import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
// import axios from "axios";
import { RootState } from "..";
import enhancedApiClient from "../../services/enhancedApiClient";
import { WorkflowStage, WorkflowTemplate } from "../../types/workflow";



interface WorkflowState {
  templates: WorkflowTemplate[];
  loading: boolean;
  error: string | null;
  selectedTemplate: WorkflowTemplate | null;
}

const initialState: WorkflowState = {
  templates: [],
  loading: false,
  error: null,
  selectedTemplate: null,
};

// Async thunks
export const fetchWorkflowTemplates = createAsyncThunk(
  "workflow/fetchTemplates",
  async () => {
    const response = await enhancedApiClient.get("/workflows");
    return response.data.data || [];
  }
);

export const createWorkflowTemplate = createAsyncThunk(
  "workflow/createTemplate",
  async (template: Partial<WorkflowTemplate>) => {
    const response = await enhancedApiClient.post("/workflows/create", template);
    return response.data.data;
  }
);

export const updateWorkflowTemplate = createAsyncThunk(
  "workflow/updateTemplate",
  async ({
    id,
    template,
  }: {
    id: string;
    template: Partial<WorkflowTemplate>;
  }) => {
    // const response = await enhancedApiClient.put(`/workflows/${id}`, template);
    // return response.data.data;
  }
);

export const deleteWorkflowTemplate = createAsyncThunk(
  "workflow/deleteTemplate",
  async (id: string) => {
    await enhancedApiClient.delete(`/workflows/${id}`);
    return id;
  }
);

export const reorderWorkflowStages = createAsyncThunk(
  "workflow/reorderStages",
  async ({
    templateId,
    stages,
  }: {
    templateId: string;
    stages: WorkflowStage[];
  }) => {
    const response = await enhancedApiClient.put(
      `/workflows/${templateId}/reorder`,
      { stages }
    );
    return response.data.data;
  }
);

const workflowSlice = createSlice({
  name: "workflow",
  initialState,
  reducers: {
    setSelectedTemplate: (state, action) => {
      state.selectedTemplate = action.payload;
    },
    clearSelectedTemplate: (state) => {
      state.selectedTemplate = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch templates
      .addCase(fetchWorkflowTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkflowTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = action.payload;
      })
      .addCase(fetchWorkflowTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch workflow templates";
      })
      // Create template
      .addCase(createWorkflowTemplate.fulfilled, (state, action) => {
        state.templates?.push(action.payload);
      })
      // Update template
      .addCase(updateWorkflowTemplate.fulfilled, (state, action) => {
        const index = state.templates.findIndex(
          (t) => t.id === action.payload.id
        );
        if (index !== -1) {
          state.templates[index] = action.payload;
        }
      })
      // Delete template
      .addCase(deleteWorkflowTemplate.fulfilled, (state, action) => {
        state.templates = state.templates.filter(
          (t) => t.id !== action.payload
        );
        if (state.selectedTemplate?.id === action.payload) {
          state.selectedTemplate = null;
        }
      })
      // Reorder stages
      .addCase(reorderWorkflowStages.fulfilled, (state, action) => {
        const template = state.templates.find(
          (t) => t.id === action.payload.id
        );
        if (template) {
          template.stages = action.payload.stages;
        }
        if (state.selectedTemplate?.id === action.payload.id) {
          state.selectedTemplate = action.payload;
        }
      });
  },
});

// Selectors
export const selectWorkflowTemplates = (state: RootState) =>
  state.workflow.templates;
export const selectWorkflowLoading = (state: RootState) =>
  state.workflow.loading;
export const selectWorkflowError = (state: RootState) => state.workflow.error;
export const selectSelectedTemplate = (state: RootState) =>
  state.workflow.selectedTemplate;

export const { setSelectedTemplate, clearSelectedTemplate } =
  workflowSlice.actions;

export default workflowSlice.reducer;
